import { IEnabledFeatures } from 'fitbeat.models';
import React, { FunctionComponent, useRef, useState } from 'react';
import { translate } from 'react-i18next';
import { StyleSheet, ScrollView } from 'react-native';
import { connect } from 'react-redux';
import { IAppStore } from '../../HomeWorkouts';
import { getWorkoutSectionTiles, TileType, WorkoutSectionTile } from '../../Planner/plannerSelectors';
import Theme from '../../theme';
import { defaultScale } from '../../utils';
import { createBookingServiceClient } from '../../utils/apiClientFactory';
import BookWorkoutSliderCard from './BookWorkoutSliderCard';
import { DashboardSection } from './DashboardSection';
import { HorizontalScroller } from './HorizontalScroller';
import { InfluencerScheduleSliderCard } from './InfluencerScheduleSliderCard';
import InfluencerWorkoutSliderCard from './InfluencerWorkoutSliderCard';
import StrengthAssessmentSliderCard from './StrengthAssessmentSliderCard';
import UpcomingWorkoutsSliderCard from './UpcomingWorkoutsSliderCard';
import WaitlistSliderCard from './WaitlistSliderCard';
import WorkoutBookingSliderCard from './WorkoutBookingSliderCard';

interface IProps extends IDispatchProps {
  t: (key: string) => string;
  navigation: any;
  userProfile: any;
}

interface IDispatchProps {
  enabledFeatures: IEnabledFeatures;
  workoutSectionTiles?: WorkoutSectionTile[];
  hasAccessToAStudioGym: boolean;
}

const WorkoutSlider: FunctionComponent<IProps> = (
  {
    t,
    navigation,
    userProfile,
    workoutSectionTiles,
  },
) => {
  const scrollViewRef = useRef<ScrollView>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!workoutSectionTiles || workoutSectionTiles.length === 0) {
    return <></>;
  }
  const showOneWideCard = workoutSectionTiles.length === 1;
  const bookingServiceClient = createBookingServiceClient();

  return (
    <DashboardSection
      heading={t('workouts')}
      isLoading={false}
    >
      <HorizontalScroller
        ref={scrollViewRef}
        currentIndex={currentIndex}
        onIndexChange={setCurrentIndex}
        totalItems={workoutSectionTiles.length}
      >
        {workoutSectionTiles.map((tile, index) => {
          const lastCardInSlider = index === workoutSectionTiles.length - 1;
          if (tile.type === TileType.strengthAssessmentTile) {
            return (
              <StrengthAssessmentSliderCard
                t={t}
                onlyOneCardInSlider={showOneWideCard}
                lastCardInSlider={lastCardInSlider}
                key={index}
                userProfile={userProfile}
              />
            );
          }

          if (tile.type === TileType.waitListEntry) {
            return (
              <WaitlistSliderCard
                userProfile={userProfile}
                t={t}
                onlyOneSession={showOneWideCard}
                lastCard={lastCardInSlider}
                navigation={navigation}
                key={index}
                waitlistEntry={tile.data}
              />
            );
          }

          if (tile.type === TileType.workoutSession) {
            return (
              <WorkoutBookingSliderCard
                onlyOneCardInSlider={showOneWideCard}
                lastCardInSlider={lastCardInSlider}
                workoutSession={tile.data}
                userProfile={userProfile}
                t={t}
                navigation={navigation}
                bookingServiceClient={bookingServiceClient}
                key={index}
              />
            );
          }

          if (tile.type === TileType.marketingTile) {
            return (
              <InfluencerScheduleSliderCard
                t={t}
                tileData={tile.data}
                navigation={navigation}
                onlyOneCardInSlider={showOneWideCard}
                lastCardInSlider={lastCardInSlider}
                key={index}
              />
            );
          }

          if (tile.type === TileType.bookWorkout) {
            return (
              <BookWorkoutSliderCard
                onlyOneCardInSlider={showOneWideCard}
                lastCardInSlider={lastCardInSlider}
                userProfile={userProfile}
                t={t}
                navigation={navigation}
                key={index}
              />
            );
          }

          if (tile.type === TileType.upcomingWorkouts) {
            return (
              <UpcomingWorkoutsSliderCard
                navigation={navigation}
                onlyOneCardInSlider={showOneWideCard}
                lastCardInSlider={lastCardInSlider}
                t={t}
                key={index}
              />
            );
          }

          if (tile.type === TileType.inviteTile) {
            return (
              <WorkoutBookingSliderCard
                onlyOneCardInSlider={showOneWideCard}
                lastCardInSlider={lastCardInSlider}
                workoutSession={tile.data}
                userProfile={userProfile}
                t={t}
                navigation={navigation}
                bookingServiceClient={bookingServiceClient}
                key={index}
                isInvite
              />
            );
          }

          if (tile.type === TileType.influencerInviteTile) {
            return (
              <InfluencerWorkoutSliderCard
                onlyOneCardInSlider={showOneWideCard}
                lastCardInSlider={lastCardInSlider}
                workoutSession={tile.data.workoutSession}
                influencerName={tile.data.influencerDetails.fullname}
                navigation={navigation}
                userProfile={userProfile}
                influencerUserId={tile.data.workoutSession.userProfileId}
                t={t}
              />
            );
          }

          if (tile.type === TileType.bookedInfluencerInviteTile) {
            return (
              <InfluencerWorkoutSliderCard
                onlyOneCardInSlider={showOneWideCard}
                lastCardInSlider={lastCardInSlider}
                workoutSession={tile.data.workoutSession}
                influencerName={tile.data.influencerData.name}
                navigation={navigation}
                userProfile={userProfile}
                influencerUserId={
                  tile.data.workoutSession.pairedSessionUserProfileId || tile.data.workoutSession.userProfileId
                }
                t={t}
                bookedInfluencerWorkout
              />
            );
          }

          return <></>;
        })}
      </HorizontalScroller>
    </DashboardSection>
  );
};

export const workoutSliderCardStyle = StyleSheet.create({
  container: {
    backgroundColor: Theme.BLACK,
    flex: 1,
    justifyContent: 'flex-end',
    padding: defaultScale(20),
    paddingBottom: defaultScale(15),
  },
});

const mapStateToProps = (state: IAppStore, props: IProps): IDispatchProps => ({
  workoutSectionTiles: getWorkoutSectionTiles(state, props.userProfile),
});

export default translate(['dashboard'], { wait: true })(connect(mapStateToProps)(WorkoutSlider));
